import React, { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { SECURITY_POLICIES } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from "../models/ColumnDefinitions/SecurityPoliciesColumns";
import { Filter } from "../models/Filter";
import LookupTypes from "../models/LookupTypes";
import searchColumns from "../models/search/SecurityPoliciesSearch";
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteSecurityPolicy, getSecurityPolicies } from "../services/securityPolicyService";
import { getSiteNamesWithPagination } from "../services/siteService";

interface Props {
    setBusy: (isBusy: boolean) => void;
    categories: Category[];
    setCategories: (category: Category[]) => void;
    onFilterChange: (filter: Filter[]) => void
}

const SecurityPoliciesTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.securityPolicies);
    const lookup = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find((item) => item.accessor === "name")?.searchOnName,
        },
    ];
    const itemPath = "/access-control/configuration/security-policy/";

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteSecurityPolicy(id).then(
                (deleteResult) => {
                    resolve(deleteResult);
                    if (categories && categories.length > 0) {
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                    }
                    setBusy(false);
                },
                (deleteResult) => {
                    reject(deleteResult);
                    setBusy(false);
                },
            );
        });
    };

    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, SECURITY_POLICIES);
    }

    return (
        <TableContextProvider
            initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
            dataMethod={listparameters => getSecurityPolicies(listparameters)}
            tableDescriptor={columns}
            searchDescriptor={searchColumns}
            useTable
            usePagination
            useSearch
            useSort
            lookup={lookup}
            useFilter
            deleteMethod={onDelete} >
            <TableContextStateMonitor onFilterChange={onFilterChange} />
            <TableTab
                title={title}
                rowPath={itemPath}
                selectRows={false}
                cyName='securityPoliciesTab'/>
        </TableContextProvider>
    );
};

export default SecurityPoliciesTab;
